// What search engines read in a storefront page's head, beyond its title:
// the canonical address and the language versions (hreflang), the page's
// language, schema.org structured data (JSON-LD) and the descriptions a page
// without its own falls back to — in the page's language.
//
// Every page has one address per language: /bg/… and /en/…. A page without a
// language prefix is the English one reached by a visitor the site did not
// send elsewhere (pages/+onBeforeRender redirects by country), so its
// canonical is the /en/ address; x-default is the Bulgarian page, the shop's
// own market.
import { langFromPath, stripLang, buildContentUrl } from "./contentRoutes"
import { sanitizeText } from "./pageMeta"
import { basePrice, isPhysicalProduct } from "./productUtils"
import { activeDiscountPercent, applyDiscount } from "./discountUtils"

export const SITE = "https://aldoo.com"
const DEFAULT_LANGUAGES = ["bg", "en"]
const X_DEFAULT = "bg"

const TEXTS = {
  bg: {
    homeTitle: "Aldoo – книги, аудиокниги, софтуер и още",
    homeDescription: "Книги, аудиокниги, интерактивни детски книжки, софтуер и подаръци от български издатели и магазини – с доставка до вас.",
    siteDescription: "Aldoo – книги, аудиокниги, софтуер и подаръци от български издатели и магазини.",
    category: (name) => `${name} – разгледайте заглавията в Aldoo: книги, аудиокниги и още, с доставка до вас.`,
    allProducts: "Всички продукти",
    // /categories with no category chosen: the newest of each category — the
    // page's own t`Latest` (CategoryPage), as the server writes it in <title>
    latest: "Най-нови",
    seller: (name) => `${name} в Aldoo: всички заглавия и продукти, условия за доставка и връщане.`,
    helpTitle: "Помощен център",
    helpDescription: "Отговори за поръчки, плащане, доставка, връщане, цифрови продукти и профила ви в Aldoo.",
    home: "Начало",
  },
  en: {
    homeTitle: "Aldoo – books, audiobooks, software and more",
    homeDescription: "Books, audiobooks, interactive children's books, software and gifts from Bulgarian publishers and shops – delivered to you.",
    siteDescription: "Aldoo – books, audiobooks, software and gifts from Bulgarian publishers and shops.",
    category: (name) => `${name} – browse the titles at Aldoo: books, audiobooks and more, delivered to you.`,
    allProducts: "All products",
    latest: "Latest",
    seller: (name) => `${name} at Aldoo: all their titles and products, delivery and returns terms.`,
    helpTitle: "Help center",
    helpDescription: "Answers about orders, payment, delivery, returns, digital products and your Aldoo account.",
    home: "Home",
  },
}

/** The texts of a language (English for any other). */
export const seoTexts = (language) => TEXTS[language] || TEXTS.en

/** The language codes the site serves (pageContext.supportedLanguages), Bulgarian and English without them. */
export const siteLanguages = (supported) => {
  const codes = (Array.isArray(supported) ? supported : []).map((l) => (typeof l === "string" ? l : l?.code)).filter(Boolean)
  return codes.length ? codes : DEFAULT_LANGUAGES
}

/** A path in one language: /bg/category/x; the home page is /bg. */
export const pathIn = (language, path) => `/${language}${path === "/" ? "" : path}`

/**
 * The page's canonical address and its language versions, from the path it
 * was rendered at (the query is left out: a sort or a page of a list is the
 * same page).
 */
export function languageUrls(pathname, supported) {
  const languages = siteLanguages(supported)
  const path = stripLang(pathname || "/") || "/"
  const prefixed = langFromPath(pathname || "/")
  const language = prefixed && languages.includes(prefixed) ? prefixed : "en"
  const alternates = languages.map((code) => ({ hreflang: code, href: SITE + pathIn(code, path) }))
  const fallback = languages.includes(X_DEFAULT) ? X_DEFAULT : languages[0]
  alternates.push({ hreflang: "x-default", href: SITE + pathIn(fallback, path) })
  return { language, canonicalUrl: SITE + pathIn(language, path), alternates }
}

// ── structured data ────────────────────────────────────────────────────────

const absolute = (url) => (!url ? undefined : /^https?:\/\//.test(url) ? url : SITE + (url.startsWith("/") ? url : `/${url}`))
const firstUrlOf = (image) => {
  if (!image) return undefined
  if (typeof image === "string") return image.split("?")[0]
  const set = image.default?.[image.default.length - 1] || image.default?.[0]
  return set ? String(set).split(",")[0].trim().split(" ")[0].split("?")[0] : undefined
}

/** The site itself: the shop (Organization) and its search (WebSite) — on the home page. */
export function siteJsonLd(language) {
  const texts = seoTexts(language)
  return [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": `${SITE}/#organization`,
      name: "Aldoo",
      url: SITE,
      logo: `${SITE}/static/images/aldoo.png`,
      description: texts.siteDescription,
      email: "support@aldoo.com",
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${SITE}/#website`,
      name: "Aldoo",
      url: SITE + pathIn(language, "/"),
      inLanguage: language,
      publisher: { "@id": `${SITE}/#organization` },
      potentialAction: {
        "@type": "SearchAction",
        target: { "@type": "EntryPoint", urlTemplate: `${SITE}${pathIn(language, "/books")}?textSearch={search_term_string}` },
        "query-input": "required name=search_term_string",
      },
    },
  ]
}

/** A trail of [name, path] steps as a BreadcrumbList, the home page first. */
export function breadcrumbJsonLd(language, steps) {
  const items = [[seoTexts(language).home, "/"], ...steps].filter(([name]) => name)
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map(([name, path], index) => ({ "@type": "ListItem", position: index + 1, name, item: SITE + pathIn(language, path) })),
  }
}

const AVAILABILITY = {
  preorder: "https://schema.org/PreOrder",
  instock: "https://schema.org/InStock",
  outofstock: "https://schema.org/OutOfStock",
}

/**
 * A product page's Product (a Book for books, with its ISBN and authors), its
 * offer at the price the page shows (the active discount taken off), and its
 * rating when it has some.
 */
export function productJsonLd(product, { language, url, categoryName, categoryPath }) {
  if (!product?.pid) return []
  const price = basePrice(product)
  const discount = activeDiscountPercent(product)
  const shown = price ? applyDiscount(price.amount, discount) : null
  const fields = product.fields && typeof product.fields === "object" ? product.fields : {}
  const isbn = String(fields.isbn || "").replace(/[^0-9Xx]/g, "")
  const isBook = !!isbn || !!product.profile?.bookLike
  const authors = (product.author || []).map((a) => (typeof a === "string" ? a : a?.name)).filter(Boolean)
  const image = [firstUrlOf(product.original), firstUrlOf(product.square), firstUrlOf(product.thumb)].filter(Boolean).map(absolute)
  const availability = product.presale ? AVAILABILITY.preorder : product.in_stock === false || product.stock === 0 ? AVAILABILITY.outofstock : AVAILABILITY.instock
  const ratings = Array.isArray(product.ratings) ? product.ratings : []
  const ratingCount = ratings.reduce((sum, n, star) => sum + (star >= 1 ? Number(n) || 0 : 0), 0)

  const entry = {
    "@context": "https://schema.org",
    "@type": isBook ? ["Product", "Book"] : "Product",
    "@id": `${url}#product`,
    name: product.name,
    url,
    description: product.description ? sanitizeText(product.description, 5000) : undefined,
    image: image.length ? [...new Set(image)] : undefined,
    // Google takes a sku of at most 50 characters: the id the Merchant Center feed gives it (the API's googleId)
    sku: product.googleId || (String(product.pid || "").length <= 50 ? product.pid : undefined),
    ...(isbn.length === 13 ? { gtin13: isbn } : {}),
    ...(isBook && isbn ? { isbn } : {}),
    ...(isBook && authors.length ? { author: authors.map((name) => ({ "@type": "Person", name })) } : {}),
    ...(isBook && fields.pages && Number(fields.pages) ? { numberOfPages: Number(fields.pages) } : {}),
    ...(isBook && fields.publishDate ? { datePublished: String(fields.publishDate).slice(0, 10) } : {}),
    ...(product.publisher ? { brand: { "@type": "Brand", name: product.publisher } } : {}),
    // Google reads `category` as its own product taxonomy, not our shelf names
    ...(isBook ? { category: "Media > Books" } : {}),
    inLanguage: language,
    ...(price && shown !== null
      ? {
          offers: {
            "@type": "Offer",
            url,
            price: (shown / 100).toFixed(2),
            priceCurrency: String(price.currency).toUpperCase(),
            availability,
            itemCondition: "https://schema.org/NewCondition",
            ...(product.presale && product.releaseDate ? { availabilityStarts: String(product.releaseDate).slice(0, 10) } : {}),
            ...(product.publisher ? { seller: { "@type": "Organization", name: product.publisher } } : {}),
            ...physicalOfferTerms(product, language),
          },
        }
      : {}),
    ...(ratingCount > 0 && Number(product.avg_rating) > 0
      ? { aggregateRating: { "@type": "AggregateRating", ratingValue: Number(product.avg_rating).toFixed(1), ratingCount, bestRating: 5, worstRating: 1 } }
      : {}),
  }
  const trail = categoryName && categoryPath ? [[categoryName, categoryPath]] : []
  return [entry, breadcrumbJsonLd(language, [...trail, [product.name, buildContentUrl(product)]])]
}

const DAY = (min, max) => ({ "@type": "QuantitativeValue", minValue: min, maxValue: max, unitCode: "DAY" })

/**
 * What a digital product's offer says: delivered at once and free, and — as
 * the store's terms have it — no return once the content is supplied (the
 * buyer agrees to immediate supply at checkout, giving up the withdrawal).
 */
const digitalOfferTerms = () => ({
  shippingDetails: {
    "@type": "OfferShippingDetails",
    shippingRate: { "@type": "MonetaryAmount", value: "0.00", currency: "EUR" },
    shippingDestination: [{ "@type": "DefinedRegion", addressCountry: "BG" }],
    deliveryTime: { "@type": "ShippingDeliveryTime", handlingTime: DAY(0, 0), transitTime: DAY(0, 0) },
  },
  hasMerchantReturnPolicy: {
    "@type": "MerchantReturnPolicy",
    applicableCountry: ["BG"],
    returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
  },
})

/**
 * What a physical product's offer says about delivery and returns, from the
 * product load's `shipping` (product-load shippingOf): the cheapest delivery of
 * one copy as checkout prices it (the seller's free-shipping offer counted), to
 * the countries it ships to, in its handling and transit days; and the seller's
 * return window, the return shipping paid by the buyer (the store's returns
 * policy, as Merchant Center has it). A digital product: digitalOfferTerms.
 */
// where the seller's return terms are read (product-load returnPolicyMode): its own page, the
// text on its seller page, or Aldoo's standard return policy; none when it has not said
export const STANDARD_RETURN_POLICY_PATH = "/returns-policy"
export const STANDARD_RETURN_POLICY_TERMS_PATH = "/terms-of-use#standard-return-policy"
function returnLinkOf(product, language) {
  const shipping = product.shipping || {}
  const lang = language || "bg"
  if (shipping.returnPolicyMode === "aldoo") return { merchantReturnLink: SITE + pathIn(lang, STANDARD_RETURN_POLICY_PATH) }
  if (shipping.returnPolicyMode === "text" && product.publisherSlug) return { merchantReturnLink: `${SITE}${pathIn(lang, `/seller/${product.publisherSlug}/about`)}#return-policy` }
  return shipping.returnPolicyUrl ? { merchantReturnLink: shipping.returnPolicyUrl } : {}
}

function physicalOfferTerms(product, language) {
  const shipping = product.shipping
  // the product's own flag decides: the product load prices a (free) delivery for digital products too
  if (!isPhysicalProduct(product)) return digitalOfferTerms()
  if (!shipping) return {}
  const countries = shipping.countries?.length ? shipping.countries : ["BG"]
  const out = {}
  if (shipping.rate && typeof shipping.rate.cost === "number") {
    const transit = Number(shipping.deliveryDays) > 0 ? Number(shipping.deliveryDays) : Number(product.deliveryDays) > 0 ? Number(product.deliveryDays) : 3
    out.shippingDetails = {
      "@type": "OfferShippingDetails",
      shippingRate: { "@type": "MonetaryAmount", value: Number(shipping.rate.cost).toFixed(2), currency: String(shipping.rate.currency || "EUR").toUpperCase() },
      shippingDestination: countries.map((country) => ({ "@type": "DefinedRegion", addressCountry: country })),
      deliveryTime: { "@type": "ShippingDeliveryTime", handlingTime: DAY(0, 1), transitTime: DAY(1, transit) },
    }
  }
  if (Number(shipping.returnDays) > 0) {
    out.hasMerchantReturnPolicy = {
      "@type": "MerchantReturnPolicy",
      applicableCountry: countries,
      returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
      merchantReturnDays: Number(shipping.returnDays),
      returnMethod: "https://schema.org/ReturnByMail",
      returnFees: "https://schema.org/ReturnFeesCustomerResponsibility",
      ...returnLinkOf(product, language),
    }
  }
  return out
}

/** A seller's page: the seller as an Organization, and the trail to it. */
export function sellerJsonLd(seller, { language, url }) {
  if (!seller?.name) return []
  return [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": `${url}#seller`,
      name: seller.name,
      url,
      ...(seller.email ? { email: seller.email } : {}),
      ...(seller.phone ? { telephone: seller.phone } : {}),
    },
    breadcrumbJsonLd(language, [[seller.name, `/seller/${seller.slug}`]]),
  ]
}
